import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.solar;

export const articleSolarPaybackPeriod: Article = {
  slug: "solar-panel-payback-period-explained",
  title: "Solar Payback Period: How to Calculate Yours and What Changes It",
  description: "The national average solar payback is 6–9 years, but your actual number depends on electricity rates, net metering policy, and roof conditions. Here's the calculation that gives you a real answer.",
  category: "Solar",
  tags: ["Solar", "Energy Efficiency", "ROI", "Home Finance"],
  createdAt: "June 26, 2026",
  updatedAt: "July 3, 2026",
  lastVerified: "July 3, 2026",
  readTime: "8 min read",
  keyTakeaway: `The national average residential solar payback period is ${p.paybackPeriodYears.min}–${p.paybackPeriodYears.max} years, per NREL analysis. Your actual payback depends on three inputs: net system cost after all incentives, annual electricity production in kWh, and your local electricity rate per kWh. Higher electricity rates produce faster payback; below roughly $0.12/kWh, the case for solar weakens considerably regardless of system cost.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "National Renewable Energy Laboratory (NREL) - Residential Solar Market Data",
      url: "https://www.nrel.gov/solar"
    },
    {
      name: "U.S. Department of Energy - Going Solar in America",
      url: "https://www.energy.gov/eere/solar/going-solar-america"
    }
  ],
  keyMetrics: [
    { label: "Avg. Payback Period (NREL)", value: `${p.paybackPeriodYears.min}-${p.paybackPeriodYears.max} Years` },
    { label: "System Service Life", value: "25-30 Years" },
    { label: "Payback Accelerator", value: "High Electricity Rates" },
    { label: "Payback Killer", value: "Poor Net Metering" }
  ],
  faqs: [
    {
      question: "What happens if I sell my house before the solar panels pay for themselves?",
      answer: "If you own the panels outright, paid cash or financed with a solar loan, research by the Lawrence Berkeley National Laboratory found buyers in studied markets paid a premium for homes with owned solar. The premium varied by market and system size. A solar loan balance must be paid off at closing if the buyer doesn't assume it, which affects your net proceeds from the sale."
    },
    {
      question: "Can a buyer assume my solar loan when I sell?",
      answer: "It depends on the lender. Some solar loans are transferable if the buyer qualifies for the lender's credit requirements. Many are not, in which case the remaining balance comes out of your sale proceeds at closing. Confirm transfer terms with your lender before installing, especially if you anticipate selling within the first decade."
    }
  ],
  content: `
    <p>
      The same 8 kW solar system costs roughly the same in Arizona and Massachusetts. The payback period does not. In Arizona, where electricity runs about $0.13/kWh, that system might take 10–11 years to break even. In Massachusetts at $0.28/kWh, the same system pays back in 6–7 years. Same hardware. Completely different financial outcome, because the value of each kilowatt-hour the system produces is more than twice as high.
    </p>
    <p>
      Payback period is where the national average becomes the least useful number you can look at. Your location, your utility, and your roof determine your number. This article shows you how to calculate it.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        NREL reports the national average payback at ${p.paybackPeriodYears.min}–${p.paybackPeriodYears.max} years (you can calculate your estimated savings with our <a href="/calculators/solar/" class="text-[var(--accent)] hover:underline font-semibold">solar savings calculator</a> or read about overall installation factors in our <a href="/articles/solar-panel-costs/" class="text-[var(--accent)] hover:underline font-semibold">solar panel costs guide</a>). Your actual number = net system cost ÷ annual electricity savings. Net cost is your gross quote minus the 30% federal ITC minus any state or utility rebates. Annual savings is your system's production in kWh multiplied by your local electricity rate. Net metering policy, how your utility credits excess production, has the single largest effect on savings after electricity rate.
      </p>
      <p class="text-xs text-text-muted mt-2 m-0">
        <em>Payback range per National Renewable Energy Laboratory residential solar market data.</em>
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>The payback formula, step by step with a worked example</li>
      <li>How electricity rate affects payback more than any other variable</li>
      <li>Net metering: what it is, why some states' policies cut payback nearly in half</li>
      <li>Battery storage: what it adds to cost and when it's worth it</li>
    </ul>

    <h2>The Payback Formula</h2>
    <p>
      The calculation has two parts: your net system cost and your annual savings.
    </p>
    <p style="text-align: center; font-family: monospace; font-size: 1.05rem; font-weight: bold; margin: 1.5rem 0;">
      Payback Period (Years) = Net System Cost ÷ Annual Electricity Savings
    </p>
    <p>
      <strong>Net system cost</strong> is your installer's quote minus the 30% federal Investment Tax Credit, minus any state rebates, minus any utility rebates. On a $22,000 system with a $6,600 federal credit and a $1,000 state rebate, net cost is $14,400.
    </p>
    <p>
      <strong>Annual electricity savings</strong> is your system's projected annual production in kWh multiplied by your local electricity rate. If your system produces 9,500 kWh per year and you pay $0.22/kWh, your annual savings is $2,090.
    </p>
    <p>
      Payback: $14,400 ÷ $2,090 = 6.9 years.
    </p>
    <p>
      Change the electricity rate to $0.12/kWh and annual savings drop to $1,140. Payback becomes 12.6 years on the same system.
    </p>

    <h2>Electricity Rate: The Variable That Moves the Number Most</h2>
    <p>
      Every kilowatt-hour your system produces is a kilowatt-hour you don't buy from the utility. The higher your rate, the more each produced kWh is worth to you.
    </p>
    <p>
      States with electricity rates above $0.20/kWh, California, Massachusetts, Connecticut, Hawaii, consistently show the fastest solar payback periods in NREL data. States with rates below $0.12/kWh, Louisiana, Arkansas, Oklahoma, show the longest, often pushing beyond 12 years even with good sun exposure.
    </p>
    <p>
      Installers sometimes include projections showing what payback looks like if rates increase 3–5% annually. Those projections improve the numbers significantly. Base your decision on current rates, not rate increase assumptions, those are speculative.
    </p>

    <!--COMPONENT:info-note|label="Worth Knowing"-->
    Your production estimate from an installer reflects your roof's orientation, pitch, and local sun-hours data. South-facing roofs at 30–45 degree tilt in high-sun regions produce significantly more than east/west-facing roofs in cloudy climates. Get estimates from at least three installers. Production is where optimistic estimates show up most frequently in solar sales.
    <!--ENDOFCOMPONENT-->

    <h2>Net Metering: Why Your Utility's Policy Changes the Calculation</h2>
    <p>
      Solar systems typically overproduce during midday and underproduce at night. Net metering governs what your utility pays you for the surplus you send to the grid.
    </p>
    <p>
      Under full retail net metering, the utility credits your excess production at your retail rate: the same price you'd pay to buy electricity. If you export 4,000 kWh per year and your rate is $0.22/kWh, that's $880 in credits annually.
    </p>
    <p>
      Under avoided-cost or wholesale net metering, a structure several states have moved toward, utilities pay only what it costs them to generate power, often $0.03–$0.06/kWh. That same 4,000 kWh export earns $120–$240 instead of $880. The difference can extend your payback by several years. Check your state's current policy at dsireusa.org before signing a contract.
    </p>

    <h2>Battery Storage: What It Costs and When It Makes Sense</h2>
    <p>
      A home battery system (typically a 10–13.5 kWh unit like the Tesla Powerwall or Enphase IQ Battery) adds $10,000–$15,000 to the installation cost. That extends your payback period.
    </p>
    <p>
      Batteries make financial sense in two situations: your utility has eliminated net metering and charges high time-of-use rates, making self-consumption of stored power more valuable than exporting; or your area has frequent power outages and backup power has significant personal or practical value to you.
    </p>
    <p>
      For homes on a standard net metering plan in a reliable grid area, battery storage is not a financially efficient addition. It adds cost faster than it adds savings. Install solar first, evaluate your usage and billing patterns for a year, then decide on storage.
    </p>
    <p>
      Run your specific numbers through our <a href="/calculators/solar/" class="text-accent underline decoration-accent/30 hover:decoration-accent/60 transition-colors">Solar Payback Calculator</a> to estimate your break-even timeline based on your local electricity rate and system size.
    </p>
  `
};